import { type ChangeEvent, type FC, useState } from "react";
import {
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  CircularProgress,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Stack,
  Tooltip,
} from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import SearchIcon from "@mui/icons-material/Search";
import { useTranslation } from "react-i18next";
import { useMarketingContactsQuery } from "@/partner/entities/marketing";
import { EmptyListCard } from "@/partner/shared/components/EmptyListCard";
import { interpolationFormat } from "@/partner/shared/lib";
import { useDeviceBreakpoint } from "@/partner/shared/hooks";
import { useDebounce } from "@/shared/hooks";
import {
  CONTACT_STATUS_COLORS,
  CONTACT_STATUS_KEYS,
} from "../model/contactLabels";
import { ContactsTableToolbar } from "./ContactsTableToolbar";
import { DownloadReportButton } from "@/partner/features/reports";
import { ReportTypeType } from "@/types/graphql/partner.generated";

const SEARCH_DELAY_MS = 300;
const DEFAULT_ROWS_PER_PAGE = 25;

interface ContactsTableProps {
  listId: string;
  onImport: () => void;
}

/**
 * Контакты списка: Card + MUI Table, как ContactListsList. Таблица
 * вложена в диалог, поэтому поиск и страница живут в локальном состоянии,
 * а не в адресе: это не фильтр страницы. На мобиле видны email и статус.
 */
export const ContactsTable: FC<ContactsTableProps> = ({ listId, onImport }) => {
  const { t, i18n } = useTranslation();
  const isMobile = useDeviceBreakpoint("md");
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search.trim(), SEARCH_DELAY_MS);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [selected, setSelected] = useState<Record<string, boolean>>({});

  const { data, loading } = useMarketingContactsQuery({
    variables: {
      listId,
      search: debouncedSearch || undefined,
      page,
      rowsPerPage,
    },
    fetchPolicy: "cache-and-network",
  });

  const nodes = data?.marketingContacts.nodes ?? [];
  const count = data?.marketingContacts.count ?? 0;
  const selectedIds = nodes.map((n) => n.id).filter((id) => selected[id]);
  const numSelected = selectedIds.length;
  const allSelected = numSelected > 0 && numSelected === nodes.length;
  const indeterminate = numSelected > 0 && numSelected < nodes.length;

  const onSelectAll = (event: ChangeEvent<HTMLInputElement>) =>
    setSelected(
      event.target.checked
        ? Object.fromEntries(nodes.map((item) => [item.id, true]))
        : {}
    );

  const onSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
    setPage(0);
    setSelected({});
  };

  if (loading && !data) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Пустой список (не пустой результат поиска): сразу предлагаем импорт.
  if (count === 0 && !search && !debouncedSearch) {
    return (
      <EmptyListCard
        title={t("marketingContacts.contacts.empty")}
        action={
          <Button variant="contained" disableElevation onClick={onImport}>
            {t("marketingContacts.import.open")}
          </Button>
        }
      />
    );
  }

  return (
    <Card elevation={0}>
      {numSelected > 0 ? (
        <ContactsTableToolbar
          indeterminate={indeterminate}
          allSelected={allSelected}
          numSelected={numSelected}
          onSelectAllClick={onSelectAll}
          selectedIds={selectedIds}
          onCloseSelected={() => setSelected({})}
        />
      ) : (
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          justifyContent="space-between"
          sx={{ p: 2 }}
        >
          <TextField
            size="small"
            value={search}
            onChange={onSearchChange}
            placeholder={t("marketingContacts.contacts.search")}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              },
            }}
          />
          <DownloadReportButton
            variant="card"
            type={ReportTypeType.MarketingContactsType}
            filter={{ listId, search: debouncedSearch || undefined }}
          />
        </Stack>
      )}
      <Table>
        <TableHead>
          <TableRow>
            <TableCell padding="checkbox">
              <Checkbox
                color="default"
                indeterminate={indeterminate}
                checked={allSelected}
                onChange={onSelectAll}
              />
            </TableCell>
            <TableCell>{t("marketingContacts.contacts.email")}</TableCell>
            {!isMobile && (
              <TableCell>{t("marketingContacts.contacts.name")}</TableCell>
            )}
            {!isMobile && (
              <TableCell>{t("marketingContacts.contacts.language")}</TableCell>
            )}
            <TableCell>{t("marketingContacts.contacts.status")}</TableCell>
            {!isMobile && (
              <TableCell align="center">
                {t("marketingContacts.contacts.viewer")}
              </TableCell>
            )}
            {!isMobile && (
              <TableCell>{t("marketingContacts.contacts.createdAt")}</TableCell>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {nodes.length === 0 && (
            <TableRow>
              <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                {t("marketingContacts.contacts.notFound")}
              </TableCell>
            </TableRow>
          )}
          {nodes.map((item) => (
            <TableRow key={item.id} hover selected={Boolean(selected[item.id])}>
              <TableCell padding="checkbox">
                <Checkbox
                  color="default"
                  checked={Boolean(selected[item.id])}
                  onChange={(event) =>
                    setSelected({
                      ...selected,
                      [item.id]: event.target.checked,
                    })
                  }
                />
              </TableCell>
              <TableCell sx={{ wordBreak: "break-all" }}>
                {item.email}
              </TableCell>
              {!isMobile && <TableCell>{item.name}</TableCell>}
              {!isMobile && <TableCell>{item.language}</TableCell>}
              <TableCell>
                <Chip
                  size="small"
                  color={CONTACT_STATUS_COLORS[item.status]}
                  label={t(CONTACT_STATUS_KEYS[item.status])}
                />
              </TableCell>
              {!isMobile && (
                <TableCell align="center">
                  {item.isViewer && (
                    <Tooltip title={t("marketingContacts.contacts.viewerHint")}>
                      <PersonIcon fontSize="small" color="action" />
                    </Tooltip>
                  )}
                </TableCell>
              )}
              {!isMobile && (
                <TableCell>
                  {interpolationFormat(item.createdAt, "short", i18n.language)}
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <TablePagination
        component="div"
        count={count}
        page={page}
        onPageChange={(_event, next) => {
          setPage(next);
          setSelected({});
        }}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(event) => {
          setRowsPerPage(Number(event.target.value));
          setPage(0);
          setSelected({});
        }}
        rowsPerPageOptions={[5, 10, 25, 100]}
        labelRowsPerPage={t("common.pagination.rowsPerPage")}
        labelDisplayedRows={({ from, to, count }) =>
          t("common.pagination.displayedRows", {
            from,
            to,
            count:
              count !== -1 ? count : t("common.pagination.moreThan", { to }),
          })
        }
      />
    </Card>
  );
};
